// src/features/system/iam/api/iamApi.ts
//
// Frontend API layer for IAM User Management (v2).
// Calls the BFF /orchestrator/iam/* endpoints which proxy to the Rust backend (:20000).

import { bff } from '@/lib/http/bff';

function unwrapEnvelope<T = any>(value: T): any {
  const obj = value as any;
  if (obj && typeof obj === 'object') {
    if (obj.data && typeof obj.data === 'object') return obj.data;
    if (obj.result && typeof obj.result === 'object') return obj.result;
  }
  return obj;
}

function toArray<T = any>(...candidates: any[]): T[] {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as T[];
  }
  return [];
}

/* ─── Types ───────────────────────────────────────────────── */

export interface UserPreferences {
  language: string;  // 'en' | 'es'
}

export interface IAMUser {
  tenant_id: number;
  id_user: number;
  username: string;
  full_name: string;
  email: string;
  status: 'active' | 'inactive' | 'locked';
  roles: string[];
  preferences: UserPreferences;
  invitation_sent?: boolean;
  created_at: string;
  updated_at: string;
}

export interface IAMUserListResponse {
  items: IAMUser[];
  total: number;
}

export interface CreateUserPayload {
  username: string;
  password: string;
  full_name?: string;
  email?: string;
  roles?: string[];
  tenant_id?: number;    // superadmin override — omit to create in caller's tenant
  send_welcome?: boolean; // if true + email set, orchestrator fires welcome email after creation
  preferences?: UserPreferences;
}

export interface ProvisionUserPayload {
  email: string;          // required — becomes username; verified-email auto-link binds Google identity on first login
  tenant_id?: number;     // superadmin override — omit to provision in caller's tenant
  roles?: string[];       // defaults to ['user'] server-side
  language?: string;      // 'en' | 'es' — defaults to 'en' server-side
  send_invitation?: boolean; // if true, sends invitation email from configured sender
}

export interface UpdateProfilePayload {
  full_name?: string;
  email?: string;
  preferences?: UserPreferences;
}

export interface ChangePasswordPayload {
  current_password?: string;
  new_password: string;
}

/* ─── API Calls ───────────────────────────────────────────── */

export async function fetchIAMUsers(opts: {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<IAMUserListResponse> {
  const raw = await bff.get<any>('/orchestrator/iam/users', {
    status: opts.status,
    search: opts.search,
    limit: opts.limit ?? 50,
    offset: opts.offset ?? 0,
  });

  const body = unwrapEnvelope(raw);
  const items = toArray<IAMUser>(body?.items, body?.users, body?.rows, body);
  const total =
    typeof body?.total === 'number'
      ? body.total
      : typeof body?.count === 'number'
        ? body.count
        : items.length;

  return { items, total };
}

export async function fetchIAMUser(idUser: number): Promise<IAMUser> {
  const raw = await bff.get<any>(`/orchestrator/iam/users/${idUser}`);
  const body = unwrapEnvelope(raw);
  return (body?.user ?? body) as IAMUser;
}

export async function createIAMUser(payload: CreateUserPayload): Promise<IAMUser> {
  const raw = await bff.post<any>('/orchestrator/iam/users', payload);
  const body = unwrapEnvelope(raw);
  return (body?.user ?? body) as IAMUser;
}

/**
 * Pre-provision a federated (Google OIDC) user — SUPERADMIN tool.
 * Creates a passwordless, SSO-only account. Name/picture are auto-fetched from
 * Google OIDC claims when the user first logs in (verified-email auto-link).
 */
export async function provisionIAMUser(payload: ProvisionUserPayload): Promise<IAMUser> {
  const raw = await bff.post<any>('/orchestrator/iam/users/provision', payload);
  const body = unwrapEnvelope(raw);
  return (body?.user ?? body) as IAMUser;
}

export async function updateIAMProfile(
  idUser: number,
  payload: UpdateProfilePayload,
): Promise<IAMUser> {
  const raw = await bff.put<any>(`/orchestrator/iam/users/${idUser}`, payload);
  const body = unwrapEnvelope(raw);
  return (body?.user ?? body) as IAMUser;
}

export async function changeIAMPassword(
  idUser: number,
  payload: ChangePasswordPayload,
): Promise<{ password_changed: boolean }> {
  return bff.post<{ password_changed: boolean }>(
    `/orchestrator/iam/users/${idUser}/password`,
    payload,
  );
}

export async function deactivateIAMUser(idUser: number): Promise<IAMUser> {
  const raw = await bff.post<any>(`/orchestrator/iam/users/${idUser}/deactivate`);
  const body = unwrapEnvelope(raw);
  return (body?.user ?? body) as IAMUser;
}

export async function activateIAMUser(idUser: number): Promise<IAMUser> {
  const raw = await bff.post<any>(`/orchestrator/iam/users/${idUser}/activate`);
  const body = unwrapEnvelope(raw);
  return (body?.user ?? body) as IAMUser;
}

export async function updateIAMRoles(
  idUser: number,
  roles: string[],
): Promise<IAMUser> {
  const raw = await bff.put<any>(`/orchestrator/iam/users/${idUser}/roles`, { roles });
  const body = unwrapEnvelope(raw);
  return (body?.user ?? body) as IAMUser;
}

export interface InvalidateSessionsResponse {
  scope: 'user' | 'tenant';
  id_user?: number;
  tenant_id?: number;
  updated_sessions: number;
}

export async function invalidateIAMSessions(
  scope: 'user' | 'tenant',
  targetId: number,
): Promise<InvalidateSessionsResponse> {
  const path =
    scope === 'user'
      ? `/orchestrator/iam/sessions/invalidate/user/${targetId}`
      : `/orchestrator/iam/sessions/invalidate/tenant/${targetId}`;
  const raw = await bff.post<any>(path);
  const body = unwrapEnvelope(raw);
  return body as InvalidateSessionsResponse;
}

export async function deleteIAMUser(idUser: number): Promise<void> {
  return bff.del(`/orchestrator/iam/users/${idUser}`);
}

/* ─── Roles ───────────────────────────────────────────────── */

export interface IAMRole {
  tenant_id: number;
  role_key: string;
  display_name: string;
  description: string;
  is_system: boolean;
  permissions: string[];
  created_at: string;
}

export interface IAMRolesResponse {
  items: IAMRole[];
}

export async function fetchIAMRoles(): Promise<IAMRolesResponse> {
  const raw = await bff.get<any>('/orchestrator/iam/roles');
  const body = unwrapEnvelope(raw);
  const items = toArray<IAMRole>(body?.items, body?.roles, body?.rows, body);
  return { items };
}

export async function updateRolePermissions(
  roleKey: string,
  permissions: string[],
): Promise<IAMRole> {
  const raw = await bff.put<any>(`/orchestrator/iam/roles/${roleKey}/permissions`, { permissions });
  const body = unwrapEnvelope(raw);
  return (body?.role ?? body) as IAMRole;
}
